import React from 'react';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useLocaleStore } from 'stores/useLocaleStore';
import { SUPPORTED_LOCALES } from 'i18n/locales';
import { switchLocale } from 'modules/Common/utils/switchLocale';
import { LAYER } from 'modules/Common/utils/layers';

// Compact language control for the NavSidebar's utility band. The app has no
// global header, so the sidebar is where the conventional "top-right globe"
// lives: globe icon + the CURRENT language's endonym — the endonym doubles as
// the "you can change this" affordance and stays readable for a user stuck in
// the wrong language. Deliberately NO flags (flags encode countries, not
// languages) and the option labels are endonyms, never translated — same
// invariants as Common/form/v2/fields/LanguageField, whose store/apply
// machinery this reuses. The Preferences tab keeps its full-size field; the
// two surfaces intentionally coexist.
export default function NavLanguageSwitcher({ isCollapsed = false }) {
  const { t } = useTranslation('common');
  const locale = useLocaleStore((s) => s.locale);
  const selected = SUPPORTED_LOCALES.find((l) => l.code === (locale ?? 'en'));

  // switchLocale owns the whole transition: resource load before the store
  // update (no English flash), the app-wide overlay, and the wait for the
  // post-cache-clear refetch wave (permissions included) to settle.
  const onValueChange = (code) => switchLocale(code);

  return (
    <Select.Root
      value={locale ?? 'en'}
      onValueChange={onValueChange}
    >
      <Select.Trigger
        // Reuses the Preferences field's translated aria key — same setting,
        // same accessible name, no new translation surface.
        aria-label={t('preferences.language.ariaLabel', 'Interface language')}
        title={isCollapsed ? selected?.label : undefined}
        className={`w-full flex items-center rounded-lg text-[12px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors outline-none ${
          isCollapsed ? 'justify-center py-2 px-0' : 'gap-2 px-2.5 py-2'
        }`}
      >
        <FontAwesomeIcon
          icon={faGlobe}
          className="text-[12px]"
        />
        {!isCollapsed && (
          <>
            {/* Endonym, deliberately untranslated (see header comment). */}
            <span className="truncate text-left flex-1">{selected?.label}</span>
            <Select.Icon>
              <ChevronDownIcon className="text-slate-400" />
            </Select.Icon>
          </>
        )}
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          side={isCollapsed ? 'right' : 'top'}
          sideOffset={6}
          style={{
            minWidth: isCollapsed ? 160 : 'var(--radix-select-trigger-width)',
            // Page-anchored popover band from the layering scale — clears the
            // Clerk org-profile host and bootstrap modals, matching
            // LanguageField's dropdown.
            zIndex: LAYER.pagePopover,
          }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 max-h-[300px] overflow-hidden"
        >
          <Select.Viewport className="p-1">
            {SUPPORTED_LOCALES.map((opt) => (
              <Select.Item
                key={opt.code}
                value={opt.code}
                className="relative flex items-center px-3 py-2 text-sm rounded-md cursor-pointer select-none data-[highlighted]:bg-primaryColor/10 data-[highlighted]:outline-none"
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
                <Select.ItemIndicator className="ml-auto pl-3">
                  <CheckIcon />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
